import { type ChangeEvent, type JSX, useMemo } from "react";

import { TextField, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";

import { type AvaConversationRef } from "../Ava/useAvaConversationRefs";
import { useAvaContext } from "./AvaContext";
import { avaPeriods } from "./consts";
import { useComponentWidth } from "./hooks";
import { ConversationHistoryPeriod } from "./types";
import { extractAttachments } from "./utils";

export const AvaHistorySelect = () => {
  const { ref, width } = useComponentWidth();

  const { setConversationId, conversationId, history } = useAvaContext();

  const handleChatHistoryChanged = (event: ChangeEvent<HTMLInputElement>): void => {
    const conversationId = event.target.value;
    setConversationId(conversationId);
  };

  const newChatMode =
    Boolean(!conversationId) ||
    Object.values(history)
      .flat()
      .find((ref) => ref.conversationId === conversationId) === undefined;

  const options = useMemo<JSX.Element[]>(() => {
    const renderPeriodHeader = (period: string) => (
      <Typography
        variant="subtitle2"
        key={period}
        data-cy={`sectionHeader-${period}`}
        sx={{ my: 1, mx: 1.25, color: "text.secondary" }}
      >
        {avaPeriods[period]}
      </Typography>
    );

    const renderConversationItem = (period: string, conversation: AvaConversationRef) => (
      <MenuItem
        key={conversation.conversationId}
        value={conversation.conversationId}
        data-cy={`avaHistoryMenuItem-${period}-${conversation.conversationId}`}
      >
        <Typography noWrap sx={{ maxWidth: width }}>
          {extractAttachments(conversation.title).cleanedMessage}
        </Typography>
      </MenuItem>
    );

    const result: JSX.Element[] = [];

    Object.values(ConversationHistoryPeriod).forEach((period) => {
      if (!history[period]?.length) {
        return;
      }

      result.push(
        renderPeriodHeader(period),
        ...history[period].map((conversation: AvaConversationRef) => renderConversationItem(period, conversation))
      );
    });

    return result;
  }, [history, width]);

  return (
    <TextField
      ref={ref}
      select
      label="Chat History"
      value={newChatMode ? "" : conversationId}
      onChange={handleChatHistoryChanged}
      fullWidth
      data-cy="chat-history-select"
      placeholder="New chat"
      sx={{ alignSelf: "stretch" }}
      slotProps={{
        select: {
          MenuProps: {
            sx: { maxHeight: "460px", width, zIndex: 1201 },
          },
        },
      }}
    >
      {options}
    </TextField>
  );
};
